import { clsx } from "clsx";
import type { ReactNode } from "react";

const variantClasses = {
  default: "bg-muted text-sub",
  success:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  warning:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  info: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  brand: "bg-adara-50 text-adara-700 dark:bg-adara-900/30 dark:text-adara-400",
};

// A status with a dot reads as plain coloured text, so tables don't fill up with pills.
const dotTextClasses = {
  default: "text-dim",
  success: "text-green-700 dark:text-green-400",
  warning: "text-amber-700 dark:text-amber-400",
  error: "text-red-700 dark:text-red-400",
  info: "text-blue-700 dark:text-blue-400",
  brand: "text-adara-700 dark:text-adara-300",
};

const dotClasses = {
  default: "bg-gray-500",
  success: "bg-green-500",
  warning: "bg-amber-500",
  error: "bg-red-500",
  info: "bg-blue-500",
  brand: "bg-adara-500",
};

export interface BadgeProps {
  variant?: keyof typeof variantClasses;
  children: ReactNode;
  dot?: boolean;
  className?: string;
}

export function Badge({
  variant = "default",
  children,
  dot = false,
  className,
}: BadgeProps) {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 whitespace-nowrap",
        dot
          ? ["text-[13px]", dotTextClasses[variant]]
          : [
              "rounded-md px-2 py-0.5 text-xs font-medium",
              variantClasses[variant],
            ],
        className,
      )}
    >
      {dot && (
        <span
          className={clsx("h-1.5 w-1.5 rounded-full", dotClasses[variant])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
